import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  TextField,
  Typography,
} from "@mui/material";
import { Calendar, ChevronDown, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import "./DropdownFilter.scss";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { format, parse } from "date-fns";
import { es } from "date-fns/locale";

interface Option {
  label: string;
  value: string;
}

interface BaseDropdownFilterProps {
  id: string;
  type: "date" | "options";
  label: string;
  anchorEl: null | HTMLElement;
  open: boolean;
  onOpen: (id: string) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClose: () => void;
}

interface OptionsDropdownFilterProps extends BaseDropdownFilterProps {
  type: "options";
  options: Option[];
  selected: Option[];
  onChange: (newSelected: Option[]) => void;
}

interface CalendarDropdownFilterProps extends BaseDropdownFilterProps {
  type: "date";
  selectedDate?: Date | null;
  onDateChange?: (date: Date | undefined) => void;
}

type DropdownFilterProps =
  | OptionsDropdownFilterProps
  | CalendarDropdownFilterProps;

export const DropdownFilter = (props: DropdownFilterProps) => {
  const { id, type, label, anchorEl, open, onOpen, onClose } = props;

  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);
  const [month, setMonth] = useState<Date>(new Date());

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    if (!newValue) return;
    setValue(newValue);
  };

  const capitalize = (text: string) =>
    text.charAt(0).toUpperCase() + text.slice(1);

  const getFormattedValue = () => {
    if (!value) return "";
    try {
      const parsed = parse(value, "yyyy-MM", new Date());
      const formatted = format(parsed, "LLLL yyyy", { locale: es });
      return capitalize(formatted);
    } catch {
      return "";
    }
  };

  const [value, setValue] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  });

  useEffect(() => {
    const [y, m] = value.split("-");
    setMonth(new Date(+y, +m - 1));
  }, [value, setMonth]);

  const CustomCaptionLabel = () => {
    return (
      <Box className="month-selector">
        <Typography style={{ flex: 1 }}>{getFormattedValue()}</Typography>
        <ChevronDown size={20} />

        <input
          type="month"
          value={value}
          onChange={handleChange}
          required
          className="month-selector__input"
        />
      </Box>
    );
  };

  const filteredOptions = useMemo(() => {
    if (type === "options") {
      return props.options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      );
    }
    return [];
  }, [type, search, props]);

  useEffect(() => {
    setVisibleCount(20);
  }, [filteredOptions]);

  useEffect(() => {
    if (filteredOptions.length > visibleCount) {
      const timeout = setTimeout(() => {
        setVisibleCount((prev) => Math.min(prev + 20, filteredOptions.length));
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [visibleCount, filteredOptions]);

  const visibleOptions = filteredOptions.slice(0, visibleCount);

  const isActive = open;

  if (type === "date") {
    const { selectedDate, onDateChange } = props;
    const hasSelections = selectedDate !== undefined;

    const buttonContent = () => {
      if (selectedDate === null || selectedDate === undefined) {
        return (
          <>
            {label}{" "}
            <Calendar
              className={`button__icon ${
                isActive ? "button__icon--selected" : ""
              }`}
            />
          </>
        );
      } else {
        return (
          <>
            {id === "start" ? "Inicio: " : "Fin: "}
            {format(selectedDate, "d 'de' MMMM 'de' yyyy", { locale: es })}
            <IconButton
              disableRipple
              onClick={(event) => {
                event.stopPropagation();
                onDateChange?.(undefined);
              }}
              sx={{ margin: "0 !important", padding: "0 !important" }}
            >
              <X className="button__icon--selected" />
            </IconButton>
          </>
        );
      }
    };

    return (
      <Box>
        <Button
          className={`button ${
            isActive ? "button--selected" : hasSelections ? "button--chips" : ""
          }`}
          onClick={onOpen(id)}
          disableRipple
        >
          {buttonContent()}
        </Button>
        <Menu
          className="calendar"
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
        >
          <Box>
            <DayPicker
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, "ccccc"),
              }}
              month={month}
              onMonthChange={(newMonth) => {
                setMonth(newMonth);
                setValue(format(newMonth, "yyyy-MM"));
              }}
              disabled={{ dayOfWeek: [0] }}
              animate
              mode="single"
              selected={selectedDate ?? undefined}
              onSelect={onDateChange}
              className="calendar__month"
              components={{ CaptionLabel: CustomCaptionLabel }}
            />
          </Box>
        </Menu>
      </Box>
    );
  } else {
    const { selected, onChange } = props;

    const toggleOption = (opt: Option) => {
      const exists = selected.some((o) => o.value === opt.value);
      const newSelected = exists
        ? selected.filter((o) => o.value !== opt.value)
        : [...selected, opt];
      onChange(newSelected);
    };

    const buttonContent = () => {
      if (selected.length === 0) {
        return (
          <>
            {label}
            <ChevronDown
              className={`button__icon ${
                isActive ? "button__icon--selected" : ""
              }`}
            />
          </>
        );
      } else if (selected.length === 1) {
        return (
          <>
            {selected[0].label.length > 20
              ? selected[0].label.slice(0, 20) + "..."
              : selected[0].label}
            <IconButton
              disableRipple
              onClick={(event) => {
                event.stopPropagation();
                onChange([]);
              }}
              sx={{ margin: "0 !important", padding: "0 !important" }}
            >
              <X className="button__icon--selected" />
            </IconButton>
          </>
        );
      } else {
        return (
          <>
            {selected.length}
            {id === "unit"
              ? " Unidades"
              : id === "area"
              ? " Áreas"
              : " Categorías"}{" "}
            seleccionadas
            <IconButton
              disableRipple
              onClick={(event) => {
                event.stopPropagation();
                onChange([]);
              }}
              sx={{ margin: "0 !important", padding: "0 !important" }}
            >
              <X className="button__icon--selected" />
            </IconButton>
          </>
        );
      }
    };

    const hasSelections = selected.length > 0;

    return (
      <Box>
        <Button
          type="button"
          className={`button ${
            isActive ? "button--selected" : hasSelections ? "button--chips" : ""
          }`}
          onClick={onOpen(id)}
          disableRipple
        >
          {buttonContent()}
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
          <Box className="menu">
            <Box className="menu__title">
              <Box className="menu__title__name">
                <Typography>
                  {label === "Unidad"
                    ? "Unidades"
                    : label === "Área"
                    ? "Áreas"
                    : "Categorías"}
                </Typography>
              </Box>
              <Button
                className="menu__title__clean"
                onClick={() => onChange([])}
                disableRipple
              >
                <Typography>Limpiar todo</Typography>
              </Button>
            </Box>

            <Box className="menu__search">
              <Search className="menu__search__icon" />
              <TextField
                type="text"
                variant="standard"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                }}
                placeholder="Buscar"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </Box>
            <Box className="menu__checkbox-container" sx={{ overflow: "auto" }}>
              <FormGroup className="menu__checkbox-container__options">
                {visibleOptions.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    sx={{
                      alignItems: "flex-start",
                      marginBottom: "2px",
                    }}
                    control={
                      <Checkbox
                        disableRipple
                        checked={selected.includes(opt)}
                        onChange={() => {
                          toggleOption(opt);
                        }}
                        sx={{
                          marginTop: "2px",
                        }}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>
        </Menu>
      </Box>
    );
  }
};
